import {
  useBlockContext,
  useEditor,
  useInspectorControl,
  useSmartProp,
} from "@smartfire/js-block-engine";
import * as wpData from "@wordpress/data";

import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { SmartFormSetup } from "./SmartFormSetup";
import { SmartForm } from "./SmartForm";
import { FormProps, FormRender } from "./FormRender";
import { SmartFormInspector } from "./editor/SmartFormInspector";
import { SmartFormField } from "./SmartFormField";

import isEqual from "lodash/isEqual";

interface Block {
  clientId: string;
  innerBlocks: Block[];
  attributes: Record<string, any>;
}

function withInnerBlocks(blocks: Block[], block: Block) {
  if (!blocks.includes(block)) {
    blocks.push(block);
    block.innerBlocks.forEach((b) => withInnerBlocks(blocks, b));
  }
  return blocks;
}

export type SmartFormHookSetup = {
  smartProp: string;
  editor?: {
    /**
     * Enable the inspector control.
     * @default true
     */
    withInspector?: boolean;
  };
};

export type FieldsAction = {
  type: "REGISTER" | "UNREGISTER";
  field: SmartFormField;
};

export function useSmartForm({ smartProp, editor }: SmartFormHookSetup) {
  const blockContext = useBlockContext();
  const { isEditing } = useEditor();

  if (!blockContext) {
    throw new Error("We are outside of a block context");
  }

  if (!blockContext.Block.smartProps?.[smartProp]) {
    throw new Error(
      `SmartForm's prop ${smartProp} is not defined in the block`
    );
  }

  if (blockContext.Block.smartProps?.[smartProp]?.type !== "object") {
    throw new Error(`SmartForm's prop ${smartProp} is not an 'object' type`);
  }

  const blockId = blockContext.attributes.__gutenbergEditor?.clientId;

  type FormSetupAttribute = SmartFormSetup & {
    "__smartform.version": string;
    "__smartform.kind": "form";
  };

  const [formSetup, setFormSetup] = useSmartProp<
    FormSetupAttribute
  >(smartProp);

  const updateFormSetup = useCallback((setup: Partial<FormSetupAttribute>) => {
    const nextValue: FormSetupAttribute = {
      ...formSetup,
      ...setup,
      "__smartform.version": "1.0.0",
      "__smartform.kind": "form",
    };
    if (!isEqual(formSetup, nextValue)) {
      setFormSetup(nextValue);
    }
  }, [formSetup, setFormSetup]);

  const formApi = useMemo(() => {
    return new SmartForm(formSetup, (newSetup) => {
      if (isEditing)
        updateFormSetup({
          ...newSetup,
          "__smartform.version": "1.0.0",
          "__smartform.kind": "form",
        });
    });
  }, [formSetup, updateFormSetup]);

  const [openModal, setOpenModal] = useState(false);

  useInspectorControl(
    () =>
      editor?.withInspector === false ? (
        <></>
      ) : (
        <SmartFormInspector
          formApi={formApi}
          openModal={openModal}
          setOpenModal={setOpenModal}
        />
      ),
    [formApi, editor, openModal, setOpenModal]
  );

  const [fields, setFields] = useState<SmartFormField[]>([]);

  const refreshFields = useCallback(() => {
    const currentBlock = wpData.select("core/block-editor").getBlock(blockId);
    if (currentBlock) {
      const blocks = withInnerBlocks([], currentBlock);
      const currentFields: SmartFormField[] = [];
      blocks.forEach((block) => {
        const attributes = block.attributes;
        Object.keys(attributes).forEach((key) => {
          if (
            attributes[key]["__smartform.version"] &&
            attributes[key]["__smartform.kind"] === "field"
          ) {
            currentFields.push(attributes[key]);
          }
        });
      });
      if (!isEqual(currentFields, fields)) {
        setFields(currentFields);
      }
    }
  }, [blockId, fields]);

  useEffect(() => {
    if (isEditing) {
      return wpData.subscribe(refreshFields);
    }
  }, [refreshFields]);

  useEffect(() => {
    if (isEditing) {
      const i = setTimeout(refreshFields, 300);
      return () => clearTimeout(i);
    }
    return () => {};
  }, []);

  useEffect(() => {
    if (isEditing) {
      updateFormSetup({
        fields,
      });
    }
  }, [fields, updateFormSetup]);

  const Form = useMemo(() => {
    return React.forwardRef<HTMLDivElement | HTMLFormElement>(
      (props: FormProps, ref) => (
        <FormRender ref={ref} formApi={formApi} {...props} />
      )
    );
  }, [isEditing ? null : formApi]);

  return {
    formApi,
    Form,
  };
}
